import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
	BookOpen,
	CalendarDays,
	CheckCircle2,
	ClipboardCheck,
	Clock3,
	Flame,
	Home,
	Info,
	Layers,
	RefreshCcw,
	TimerReset,
} from "lucide-react";
import { createFlashcardSession, getFlashcardResultsPayload } from "utils/supabase";

const ratingColors = {
	again: "#ef4444",
	hard: "#f59e0b",
	good: "#3b82f6",
	easy: "#22c55e",
};

function formatTime(seconds = 0) {
	const safeSeconds = Math.max(0, Number(seconds) || 0);
	const minutes = Math.floor(safeSeconds / 60);
	const secs = safeSeconds % 60;

	return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function SectionCard({ children, sx }) {
	return (
		<Paper
			variant="outlined"
			sx={{
				borderColor: "#dfe5ee",
				borderRadius: "12px",
				boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
				p: { xs: 2, md: 2.5 },
				height: "100%",
				...sx,
			}}
		>
			{children}
		</Paper>
	);
}

function SectionTitle({ icon: Icon, children }) {
	return (
		<Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
			<Box component="span" sx={{ color: "#2563eb", display: "inline-flex" }}>
				<Icon size={20} strokeWidth={2.2} />
			</Box>
			<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 700 }}>
				{children}
			</Typography>
		</Stack>
	);
}

function StatCard({ icon: Icon, iconColor, iconBg, value, label, detail }) {
	return (
		<SectionCard sx={{ alignItems: "center", display: "flex", justifyContent: "center", textAlign: "center" }}>
			<Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ width: "100%" }}>
				<Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
					<Box
						component="span"
						sx={{
							alignItems: "center",
							backgroundColor: iconBg,
							borderRadius: "50%",
							color: iconColor,
							display: "flex",
							flexShrink: 0,
							height: 48,
							justifyContent: "center",
							lineHeight: 0,
							width: 48,
						}}
					>
						<Icon size={25} strokeWidth={2.2} />
					</Box>
				</Box>
				<Typography sx={{ color: "#0f172a", fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
					{value}
				</Typography>
				<Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>
					{label}
				</Typography>
				{detail && (
					<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 500 }}>
						{detail}
					</Typography>
				)}
			</Stack>
		</SectionCard>
	);
}

function HeroSummary({ payload }) {
	const summary = payload?.summary || {};
	const roundedAccuracy = Math.round(Number(summary.accuracy_percentage) || 0);
	const accuracyDisplay = String(Math.min(100, Math.max(0, roundedAccuracy)));

	return (
		<SectionCard sx={{ alignItems: "center", display: "flex" }}>
			<Stack direction="row" spacing={{ xs: 2, md: 3 }} alignItems="center" sx={{ justifyContent: "flex-start", minWidth: 0, width: "100%" }}>
				<Box sx={{ display: "flex", flexShrink: 0, fontSize: { xs: 58, sm: 72, md: 88 }, justifyContent: "flex-start", lineHeight: 1, width: "auto" }}>🏆</Box>
				<Box sx={{ alignItems: "flex-start", borderLeft: "1px solid #dfe5ee", display: "flex", flexDirection: "column", minWidth: 0, pl: { xs: 2, md: 3 }, textAlign: "left", width: "100%" }}>
					<Typography sx={{ color: "#0f172a", fontSize: 14, fontWeight: 700, mb: 1 }}>
						Precisión
					</Typography>
					<Typography sx={{ color: "#2563eb", fontSize: { xs: "clamp(22px, 9vw, 34px)", md: 42 }, fontWeight: 800, lineHeight: 1, maxWidth: "100%", whiteSpace: "nowrap" }}>
						{accuracyDisplay}%
					</Typography>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ color: "#10b981", mt: 1.25 }}>
						<CheckCircle2 size={18} />
						<Typography sx={{ fontSize: 13, fontWeight: 700 }}>Sesión completada</Typography>
					</Stack>
				</Box>
			</Stack>
		</SectionCard>
	);
}

function RatingDistribution({ distribution, totalCards }) {
	const entries = ["again", "hard", "good", "easy"].map((key) => ({ key, ...(distribution?.[key] || {}) }));
	const gradient = entries
		.reduce((segments, item, index) => {
			const previous = segments.total;
			const percentage = Number(item.percentage) || 0;
			segments.parts.push(`${ratingColors[item.key]} ${previous}% ${previous + percentage}%`);
			segments.total += percentage;
			if (index === entries.length - 1 && segments.total < 100) {
				segments.parts.push(`#e5e7eb ${segments.total}% 100%`);
			}
			return segments;
		}, { total: 0, parts: [] }).parts.join(", ");

	return (
		<SectionCard sx={{ display: "flex", flexDirection: "column" }}>
			<SectionTitle icon={ClipboardCheck}>Distribución de calificaciones</SectionTitle>
			<Box sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center" }}>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
					<Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-start" }, width: { xs: "100%", sm: "auto" } }}>
						<Box
							sx={{
								alignItems: "center",
								background: `conic-gradient(${gradient})`,
								borderRadius: "50%",
								display: "flex",
								height: 154,
								justifyContent: "center",
								width: 154,
							}}
						>
							<Box sx={{ alignItems: "center", bgcolor: "white", borderRadius: "50%", display: "flex", flexDirection: "column", height: 116, justifyContent: "center", width: 116 }}>
								<Typography sx={{ color: "#0f172a", fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{totalCards || 0}</Typography>
								<Typography sx={{ color: "#334155", fontSize: 11, fontWeight: 600, textAlign: "center" }}>Tarjetas revisadas</Typography>
							</Box>
						</Box>
					</Box>
					<Stack spacing={1.5} sx={{ flex: 1, width: "100%" }}>
						{entries.map((item) => (
							<Stack key={item.key} direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
								<Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
									<Box sx={{ bgcolor: ratingColors[item.key], borderRadius: "50%", flexShrink: 0, height: 10, width: 10 }} />
									<Typography sx={{ color: "#334155", fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</Typography>
								</Stack>
								<Typography sx={{ color: "#0f172a", flexShrink: 1, fontSize: { xs: 11, sm: 12 }, fontWeight: 700, ml: "auto", minWidth: 0, textAlign: "right", whiteSpace: "nowrap" }}>{item.count || 0} ({item.percentage || 0}%)</Typography>
							</Stack>
						))}
					</Stack>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ color: "#64748b", mt: 2 }}>
					<Info size={15} />
					<Typography sx={{ fontSize: 11, fontWeight: 500 }}>Las tarjetas se muestran según el sistema de repetición espaciada.</Typography>
				</Stack>
			</Box>
		</SectionCard>
	);
}

function NextReviews({ nextReviews }) {
	const rows = ["tomorrow", "in_3_days", "in_7_days"].map((key) => nextReviews?.[key]).filter(Boolean);

	return (
		<SectionCard sx={{ display: "flex", flexDirection: "column" }}>
			<SectionTitle icon={CalendarDays}>Próximos repasos</SectionTitle>
			<Stack spacing={1.5} sx={{ flex: 1, justifyContent: "center" }}>
				{rows.map((row, index) => (
					<Box key={row.label} sx={{ alignItems: "center", columnGap: 2, display: "grid", gridTemplateColumns: "42px minmax(0, 1fr) auto", minHeight: 42 }}>
						<Box sx={{ alignItems: "center", bgcolor: ["#fff7ed", "#eff6ff", "#f5f3ff"][index], borderRadius: "10px", color: ["#f97316", "#2563eb", "#8b5cf6"][index], display: "flex", flexShrink: 0, height: 42, justifyContent: "center", lineHeight: 0, width: 42 }}>
							<CalendarDays size={21} />
						</Box>
						<Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{row.label}</Typography>
						<Typography sx={{ color: "#475569", fontSize: 12, fontWeight: 600, lineHeight: 1.2, textAlign: "right", whiteSpace: "nowrap" }}>{row.count || 0} tarjetas</Typography>
					</Box>
				))}
			</Stack>
		</SectionCard>
	);
}

function PerformanceList({ title, icon, items, labelKey, maxItems = 4 }) {
	const Icon = icon;
	const visibleItems = Array.isArray(items) ? items.slice(0, maxItems) : [];

	return (
		<SectionCard sx={{ display: "flex", flexDirection: "column" }}>
			<SectionTitle icon={Icon}>{title}</SectionTitle>
			<Stack spacing={2} sx={{ flex: 1, justifyContent: "center" }}>
				{visibleItems.length === 0 && (
					<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 500 }}>Sin datos suficientes.</Typography>
				)}
				{visibleItems.map((item) => {
					const percentage = Number(item.accuracy_percentage) || 0;
					return (
						<Stack key={item.specialty_id || item.topic_id || item[labelKey]} spacing={0.75}>
							<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
								<Typography sx={{ color: "#0f172a", fontSize: 12, fontWeight: 700 }}>{item[labelKey]}</Typography>
								<Typography sx={{ color: "#0f172a", fontSize: 12, fontWeight: 700 }}>{percentage}% ({item.correct_cards}/{item.reviewed_cards})</Typography>
							</Stack>
							<LinearProgress
								variant="determinate"
								value={percentage}
								sx={{
									bgcolor: "#e5eaf2",
									borderRadius: 999,
									height: 9,
									"& .MuiLinearProgress-bar": {
										bgcolor: percentage >= 80 ? "#22c55e" : percentage >= 70 ? "#f59e0b" : "#ef4444",
										borderRadius: 999,
									},
								}}
							/>
						</Stack>
					);
				})}
			</Stack>
		</SectionCard>
	);
}

function CardStateSummary({ stateSummary }) {
	const items = [
		{ label: "Nuevas", value: stateSummary?.new_cards || 0, caption: "Por aprender", color: "#16a34a" },
		{ label: "En aprendizaje", value: stateSummary?.learning_cards || 0, caption: "Revisadas", color: "#2563eb" },
		{ label: "Dominadas", value: stateSummary?.mastered_cards || 0, caption: "Consolidadas", color: "#7c3aed" },
	];

	return (
		<SectionCard>
			<SectionTitle icon={Layers}>Resumen de tarjetas</SectionTitle>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={0} sx={{ width: "100%" }}>
				{items.map((item, index) => (
					<Box key={item.label} sx={{ borderLeft: { sm: index === 0 ? 0 : "1px solid #dfe5ee" }, flex: 1, minWidth: 0, px: 0.75, textAlign: "center" }}>
						<Typography sx={{ color: item.color, fontSize: 20, fontWeight: 800 }}>{item.value}</Typography>
						<Typography sx={{ color: "#0f172a", fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</Typography>
						<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.caption}</Typography>
					</Box>
				))}
			</Stack>
		</SectionCard>
	);
}

export default function ResultadosFlashcards() {
	const { sessionId } = useParams();
	const history = useHistory();
	const [payload, setPayload] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreatingIncorrectSession, setIsCreatingIncorrectSession] = useState(false);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	}, [sessionId]);

	useEffect(() => {
		let mounted = true;

		async function fetchResults() {
			setIsLoading(true);
			try {
				const { data, error } = await getFlashcardResultsPayload(sessionId);
				if (error) {
					console.error("Error fetching flashcard results payload:", error);
					return;
				}

				if (mounted) {
					setPayload(data);
				}
			} catch (error) {
				console.error("Unexpected error fetching flashcard results payload:", error);
			} finally {
				if (mounted) {
					setIsLoading(false);
				}
			}
		}

		fetchResults();

		return () => {
			mounted = false;
		};
	}, [sessionId]);

	const summary = payload?.summary || {};
	const actions = payload?.actions || {};

	const handleCreateIncorrectSession = async () => {
		if (isCreatingIncorrectSession) {
			return;
		}

		setIsCreatingIncorrectSession(true);
		try {
			const { data, error } = await createFlashcardSession([], 20, "incorrect");
			if (error) {
				console.error("Error creating incorrect flashcard session:", error);
				return;
			}

			const createdSessionId = data?.flashcard_session_id || data?.[0]?.flashcard_session_id;
			if (!createdSessionId) {
				console.error("No flashcard session id returned from create_flashcard_session", data);
				return;
			}

			history.push(`/admin/flashcards/${createdSessionId}`);
		} catch (error) {
			console.error("Unexpected error creating incorrect flashcard session:", error);
		} finally {
			setIsCreatingIncorrectSession(false);
		}
	};

	if (isLoading) {
		return (
			<div className="w-full px-4 mt-4">
				<SectionCard sx={{ textAlign: "center" }}>
					<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>Cargando resultados...</Typography>
				</SectionCard>
			</div>
		);
	}

	return (
		<div className="flex flex-wrap mt-4">
			<div className="w-full px-4 mb-4">
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "repeat(2, minmax(0, 1fr))",
							sm: "repeat(2, minmax(0, 1fr))",
							md: "repeat(5, minmax(0, 1fr))",
							lg: "repeat(7, minmax(0, 1fr))",
						},
						"@media (min-width: 768px)": {
							gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
						},
						"@media (min-width: 1200px)": {
							gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
						},
					}}
				>
					<Box sx={{ gridColumn: { xs: "span 2", sm: "span 2", md: "span 5", lg: "span 2" }, "@media (min-width: 768px)": { gridColumn: "span 5" }, "@media (min-width: 1200px)": { gridColumn: "span 2" } }}>
						<HeroSummary payload={payload} />
					</Box>
					<StatCard icon={ClipboardCheck} iconColor="#2563eb" iconBg="#dbeafe" value={summary.reviewed_cards || 0} label="Tarjetas revisadas" detail="100% del total" />
					<StatCard icon={CheckCircle2} iconColor="#16a34a" iconBg="#dcfce7" value={summary.correct_cards || 0} label="Correctas" detail={`${summary.accuracy_percentage || 0}%`} />
					<StatCard icon={TimerReset} iconColor="#f97316" iconBg="#ffedd5" value={summary.again_cards || 0} label="Otra vez" detail={`${payload?.rating_distribution?.again?.percentage || 0}%`} />
					<StatCard icon={Clock3} iconColor="#7c3aed" iconBg="#f3e8ff" value={formatTime(summary.time_total_seconds)} label="Tiempo total" detail={`Prom. ${summary.avg_time_per_card_seconds || 0} s/tarjeta`} />
					<Box sx={{ gridColumn: { xs: "span 2", sm: "span 2", md: "auto", lg: "auto" }, "@media (min-width: 768px)": { gridColumn: "auto" } }}>
						<StatCard icon={Flame} iconColor="#f59e0b" iconBg="#fef3c7" value={summary.best_streak || 0} label="Racha máxima" detail="¡Nuevo récord!" />
					</Box>
				</Box>
			</div>

			<div className="w-full px-4 mb-4">
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(2, minmax(0, 1fr))",
							lg: "repeat(12, minmax(0, 1fr))",
						},
						"@media (min-width: 768px)": {
							gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
						},
						"@media (min-width: 1200px)": {
							gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
						},
					}}
				>
					<Box sx={{ gridColumn: { md: "span 1", lg: "span 4" }, "@media (min-width: 768px)": { gridColumn: "span 1" }, "@media (min-width: 1200px)": { gridColumn: "span 4" } }}>
						<RatingDistribution distribution={payload?.rating_distribution} totalCards={summary.reviewed_cards} />
					</Box>
					<Box sx={{ gridColumn: { md: "span 1", lg: "span 3" }, "@media (min-width: 768px)": { gridColumn: "span 1" }, "@media (min-width: 1200px)": { gridColumn: "span 3" } }}>
						<NextReviews nextReviews={payload?.next_reviews} />
					</Box>
					<Box sx={{ gridColumn: { md: "span 2", lg: "span 5" }, "@media (min-width: 768px)": { gridColumn: "span 2" }, "@media (min-width: 1200px)": { gridColumn: "span 5" } }}>
						<PerformanceList title="Rendimiento por materia" icon={BookOpen} items={payload?.performance_by_specialty} labelKey="specialty_name" />
					</Box>
				</Box>
			</div>

			<div className="w-full px-4 mb-4">
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							lg: "repeat(12, minmax(0, 1fr))",
						},
					}}
				>
					<Box sx={{ gridColumn: { lg: "span 4" } }}>
						<CardStateSummary stateSummary={payload?.card_state_summary} />
					</Box>
					<Box sx={{ gridColumn: { lg: "span 8" } }}>
						<SectionCard sx={{ alignItems: "center", display: "flex" }}>
							<Stack direction={{ xs: "column", md: "row" }} spacing={2.5} sx={{ width: "100%" }}>
								<Button
									variant="contained"
									disabled={!actions.can_review_again_cards || isCreatingIncorrectSession}
									onClick={handleCreateIncorrectSession}
									startIcon={<RefreshCcw size={19} />}
									sx={{ bgcolor: "#2563eb", borderRadius: "10px", flex: 1, fontSize: 13, fontWeight: 600, py: 1.25, textTransform: "none", "&:hover": { bgcolor: "#1d4ed8" } }}
								>
									Revisar tarjetas
								</Button>
								<Button
									variant="outlined"
									disabled={!actions.can_start_new_session}
									onClick={() => history.push("/admin/flashcards")}
									startIcon={<Clock3 size={19} />}
									sx={{ borderColor: "#dfe5ee", borderRadius: "10px", color: "#334155", flex: 1, fontSize: 13, fontWeight: 600, py: 1.25, textTransform: "none" }}
								>
									Nueva sesión
								</Button>
								<Button
									variant="outlined"
									disabled={!actions.can_go_dashboard}
									onClick={() => history.push("/admin/dashboard")}
									startIcon={<Home size={19} />}
									sx={{ borderColor: "#dfe5ee", borderRadius: "10px", color: "#334155", flex: 1, fontSize: 13, fontWeight: 600, py: 1.25, textTransform: "none" }}
								>
									Ir al dashboard
								</Button>
							</Stack>
						</SectionCard>
					</Box>
				</Box>
			</div>
		</div>
	);
}
